import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, X } from 'lucide-react';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  category: 'recording' | 'tournament' | 'social' | 'skill' | 'milestone';
  requirement_type: 'count' | 'score' | 'specific' | 'combo';
  requirement_value: number;
  points: number;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  is_hidden: boolean;
  sort_order: number;
  deleted_at: string | null;
  unlock_count?: number;
}

export const AdminAchievements: React.FC = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeletedFilter, setShowDeletedFilter] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const emptyAchievement: Achievement = {
    id: '',
    title: '',
    description: '',
    icon: 'trophy',
    category: 'milestone',
    requirement_type: 'count',
    requirement_value: 1,
    points: 10,
    rarity: 'common',
    is_hidden: false,
    sort_order: 0,
    deleted_at: null
  };

  useEffect(() => {
    loadAchievements();
  }, [showDeletedFilter]);

  const loadAchievements = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('achievements')
        .select('*')
        .order('sort_order', { ascending: true });

      if (!showDeletedFilter) {
        query = query.is('deleted_at', null);
      }

      const { data, error } = await query;

      if (error) throw error;

      const achievementsWithCounts = await Promise.all(
        (data || []).map(async (achievement) => {
          const { count } = await supabase
            .from('user_achievements')
            .select('*', { count: 'exact', head: true })
            .eq('achievement_id', achievement.id);

          return {
            ...achievement,
            unlock_count: count || 0
          };
        })
      );

      setAchievements(achievementsWithCounts);
    } catch (error) {
      console.error('Error loading achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editingAchievement) return;

    try {
      if (isCreating) {
        const { error } = await supabase
          .from('achievements')
          .insert({
            title: editingAchievement.title,
            description: editingAchievement.description,
            icon: editingAchievement.icon,
            category: editingAchievement.category,
            requirement_type: editingAchievement.requirement_type,
            requirement_value: editingAchievement.requirement_value,
            points: editingAchievement.points,
            rarity: editingAchievement.rarity,
            is_hidden: editingAchievement.is_hidden,
            sort_order: editingAchievement.sort_order
          });

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('achievements')
          .update({
            title: editingAchievement.title,
            description: editingAchievement.description,
            icon: editingAchievement.icon,
            category: editingAchievement.category,
            requirement_type: editingAchievement.requirement_type,
            requirement_value: editingAchievement.requirement_value,
            points: editingAchievement.points,
            rarity: editingAchievement.rarity,
            is_hidden: editingAchievement.is_hidden,
            sort_order: editingAchievement.sort_order
          })
          .eq('id', editingAchievement.id);

        if (error) throw error;
      }

      setEditingAchievement(null);
      setIsCreating(false);
      loadAchievements();
    } catch (error) {
      console.error('Error saving achievement:', error);
    }
  };

  const handleSoftDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this achievement? Users who have unlocked it will keep it.')) return;

    try {
      const { error } = await supabase
        .from('achievements')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      loadAchievements();
    } catch (error) {
      console.error('Error deleting achievement:', error);
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const { error } = await supabase
        .from('achievements')
        .update({ deleted_at: null })
        .eq('id', id);

      if (error) throw error;
      loadAchievements();
    } catch (error) {
      console.error('Error restoring achievement:', error);
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'text-gray-400';
      case 'rare': return 'text-blue-400';
      case 'epic': return 'text-purple-400';
      case 'legendary': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const getRarityBg = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-500/20';
      case 'rare': return 'bg-blue-500/20';
      case 'epic': return 'bg-purple-500/20';
      case 'legendary': return 'bg-yellow-500/20';
      default: return 'bg-gray-500/20';
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-5">
          <img
            src="/TSL_Logo.png"
            alt="Logo"
            className="w-[600px] h-[600px] object-contain"
          />
        </div>
        <div className="relative z-10 animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-kod-accent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold mb-2">Achievement Management</h1>
          <p className="text-white/70">Manage achievements and track user progress</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDeletedFilter(!showDeletedFilter)}
            className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg transition-colors"
          >
            {showDeletedFilter ? 'Hide Deleted' : 'Show Deleted'}
          </button>
          <button
            onClick={() => {
              setEditingAchievement(emptyAchievement);
              setIsCreating(true);
            }}
            className="px-4 py-2 bg-kod-accent hover:bg-kod-accent-dark text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            New Achievement
          </button>
        </div>
      </div>

      {editingAchievement && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-kod-purple-800 rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {isCreating ? 'Create Achievement' : 'Edit Achievement'}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={editingAchievement.title}
                  onChange={(e) => setEditingAchievement({ ...editingAchievement, title: e.target.value })}
                  className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={editingAchievement.description}
                  onChange={(e) => setEditingAchievement({ ...editingAchievement, description: e.target.value })}
                  className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Icon (Lucide name)</label>
                  <input
                    type="text"
                    value={editingAchievement.icon}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, icon: e.target.value })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <select
                    value={editingAchievement.category}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, category: e.target.value as any })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  >
                    <option value="recording">Recording</option>
                    <option value="tournament">Tournament</option>
                    <option value="social">Social</option>
                    <option value="skill">Skill</option>
                    <option value="milestone">Milestone</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Requirement Type</label>
                  <select
                    value={editingAchievement.requirement_type}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, requirement_type: e.target.value as any })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  >
                    <option value="count">Count</option>
                    <option value="score">Score</option>
                    <option value="specific">Specific</option>
                    <option value="combo">Combo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Requirement Value</label>
                  <input
                    type="number"
                    value={editingAchievement.requirement_value}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, requirement_value: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Points</label>
                  <input
                    type="number"
                    value={editingAchievement.points}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, points: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Rarity</label>
                  <select
                    value={editingAchievement.rarity}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, rarity: e.target.value as any })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  >
                    <option value="common">Common</option>
                    <option value="rare">Rare</option>
                    <option value="epic">Epic</option>
                    <option value="legendary">Legendary</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Sort Order</label>
                  <input
                    type="number"
                    value={editingAchievement.sort_order}
                    onChange={(e) => setEditingAchievement({ ...editingAchievement, sort_order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 bg-kod-purple-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingAchievement.is_hidden}
                  onChange={(e) => setEditingAchievement({ ...editingAchievement, is_hidden: e.target.checked })}
                  className="w-4 h-4 mr-2"
                />
                <label className="text-sm">Hidden until unlocked</label>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2 bg-kod-accent hover:bg-kod-accent-dark text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Save size={20} />
                Save
              </button>
              <button
                onClick={() => {
                  setEditingAchievement(null);
                  setIsCreating(false);
                }}
                className="px-4 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg flex items-center gap-2 transition-colors"
              >
                <X size={20} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {achievements.map((achievement) => (
          <div
            key={achievement.id}
            className={`card ${achievement.deleted_at ? 'opacity-50' : ''}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-full ${getRarityBg(achievement.rarity)} flex items-center justify-center`}>
                  <span className={`text-xl ${getRarityColor(achievement.rarity)}`}>
                    {achievement.icon === 'trophy' ? '🏆' : '⭐'}
                  </span>
                </div>
                <div>
                  <h3 className="font-bold">{achievement.title}</h3>
                  <p className="text-xs text-white/60">{achievement.unlock_count} users</p>
                </div>
              </div>
              {achievement.is_hidden && <EyeOff size={16} className="text-white/40" />}
            </div>

            <p className="text-sm text-white/70 mb-3">{achievement.description}</p>

            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`px-2 py-1 rounded text-xs ${getRarityBg(achievement.rarity)} ${getRarityColor(achievement.rarity)}`}>
                {achievement.rarity}
              </span>
              <span className="px-2 py-1 rounded text-xs bg-kod-purple-700/50">
                {achievement.category}
              </span>
              <span className="px-2 py-1 rounded text-xs bg-kod-purple-700/50">
                {achievement.points} pts
              </span>
              <span className="px-2 py-1 rounded text-xs bg-kod-purple-700/50">
                {achievement.requirement_type}: {achievement.requirement_value}
              </span>
            </div>

            <div className="flex gap-2">
              {!achievement.deleted_at ? (
                <>
                  <button
                    onClick={() => {
                      setEditingAchievement(achievement);
                      setIsCreating(false);
                    }}
                    className="flex-1 px-3 py-2 bg-kod-purple-700 hover:bg-kod-purple-600 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Edit2 size={16} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleSoftDelete(achievement.id)}
                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleRestore(achievement.id)}
                  className="flex-1 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors"
                >
                  Restore
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
